import { z } from 'zod';
import { middlewareIdSchema } from './middlewares';

/**
 * Middleware allow-list (DECISIONS 2026-10-10 plug-ins #3): the Host Admin allows middlewares
 * per school on central; central pushes the list to the school (PUT /private/v1/middlewares)
 * and re-sends it until the school's /private/v1/status reports the same list.
 *
 * Ids are checked by pattern, not against this release's catalogue, so a newer central can talk
 * to an older school; the school ignores ids it doesn't know and central's Host Admin route
 * rejects ids outside its own catalogue (isKnownMiddlewareId).
 */
export const MIDDLEWARE_ALLOW_LIST_MAX = 50;

/** Sorted, de-duplicated copy (never mutates the input). */
export function normalizeAllowList(ids: readonly string[]): string[] {
  return [...new Set(ids)].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/** True when both lists hold the same ids, ignoring order and duplicates. */
export function sameAllowList(a: readonly string[], b: readonly string[]): boolean {
  const left = normalizeAllowList(a);
  const right = normalizeAllowList(b);
  return left.length === right.length && left.every((id, i) => id === right[i]);
}

/** Body of PUT /private/v1/middlewares and of Host Admin PUT …/tenants/:code/middlewares. */
export const middlewareAllowListSchema = z.object({
  allowed: z
    .array(middlewareIdSchema)
    .max(MIDDLEWARE_ALLOW_LIST_MAX)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Duplicate middleware id' }),
});
export type MiddlewareAllowList = z.infer<typeof middlewareAllowListSchema>;

/**
 * The school's middleware state as reported in GET /private/v1/status (additive, optional) and
 * returned by PUT /private/v1/middlewares. `configured` says only yes/no per allowed middleware;
 * central never sees settings (plan §6).
 */
export const schoolMiddlewaresStatusSchema = z.object({
  allowed: z.array(z.string()),
  configured: z.record(z.string(), z.boolean()),
});
export type SchoolMiddlewaresStatus = z.infer<typeof schoolMiddlewaresStatusSchema>;

/** GET / PUT /v1/admin/tenants/:code/middlewares (central, Host Admin). */
export const tenantMiddlewaresViewSchema = z.object({
  /** The list central holds for the school (sorted). */
  allowed: z.array(z.string()),
  /** What the school last reported; null until it reports middlewares. */
  reported: schoolMiddlewaresStatusSchema.nullable(),
  /** The school's reported list equals `allowed`. */
  inSync: z.boolean(),
  /** PUT only: result of pushing the list to the school now; null on GET. */
  push: z.object({ ok: z.boolean(), error: z.string().nullable() }).nullable(),
});
export type TenantMiddlewaresView = z.infer<typeof tenantMiddlewaresViewSchema>;
